/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2023 - 2023
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { isEmpty } from "lodash";

import messages from "../../../helpers/messages";
import {
  submitCapacityMatch,
  resetBannerActionState,
  refreshCapacityMatches
} from "../../../slices/bidProcurementSlice";
import {
  status as bidStatus,
  capacityMatchingStatus,
  mode
} from "../../../helpers/types";
import UnmatchedLanesModal from "../procurement/UnmatchedLanesModal";
import FinalSubmissionModal from "../procurement/FinalSubmissionModal";
import RefreshIcon from "../../icons/RefreshIcon";
import SpinnerButton from "./SpinnerButton";

function ProcurementViewInProgressState({ role, summary }) {
  const dispatch = useDispatch();
  const {
    submitCapacityMatchIsLoading,
    submitCapacityMatchFailure,
    capacityMatchLaneSummary: { capacityMatches },
    refreshCapacityMatchesIsLoading
  } = useSelector(state => state.bidProcurement);
  const route = useLocation().pathname.split("/")[3] || "";
  const [openUnmatchedLanesModal, setOpenUnmatchedLanesModal] = useState(false);
  const [openFinalSubmissionModal, setOpenFinalSubmissionModal] =
    useState(false);

  const { modes, bidId } = summary;
  const { bidModeId, capacityMatchingStatus: status } = modes.find(
    x =>
      x.mode === mode.tl &&
      x.status === bidStatus.inProgress &&
      (x.capacityMatchingStatus === capacityMatchingStatus.inProgress ||
        x.capacityMatchingStatus === capacityMatchingStatus.review)
  );
  const unmatchedLanes = capacityMatches?.filter(x => isEmpty(x.laneMatches));

  const handleFinalSubmission = () => {
    dispatch(resetBannerActionState());
    dispatch(submitCapacityMatch(bidModeId, bidId, role, route));
  };

  const handleSubmitClick = () => {
    if (unmatchedLanes?.length > 0) {
      setOpenUnmatchedLanesModal(true);
    } else {
      setOpenFinalSubmissionModal(true);
    }
  };

  return (
    <>
      <p className="inline-block font-size-14 pt-16 mb-0">
        {status === capacityMatchingStatus.inProgress
          ? messages.selectMatchesForTheFollowingLanesBannerMessage
              .defaultMessage
          : messages.procurementRepViewReviewBannerMessage.defaultMessage}
      </p>
      <div className="fr py-8 my-2">
        {refreshCapacityMatchesIsLoading ? (
          <SpinnerButton minWidth="150px" />
        ) : (
          <button
            className="text-button univers-67-bold-cond font-size-14 mx-8 h-32 text-transform-none"
            data-testid="refresh-button"
            style={{ minWidth: "150px" }}
            onClick={() => dispatch(refreshCapacityMatches(bidId, role))}
          >
            <RefreshIcon
              className="icon-right align-middle mr-4"
              width="16px"
              height="16px"
              fill="#0066fa"
            />
            {messages.refreshMatches.defaultMessage}
          </button>
        )}
        <button
          className="text-button univers-67-bold-cond font-size-14 mx-8 h-32 border-cobalt-blue"
          data-testid="submit"
          onClick={() => handleSubmitClick(bidModeId, bidId)}
          style={{ minWidth: "150px" }}
        >
          {messages.sendSubmissions.defaultMessage}
        </button>
      </div>
      <UnmatchedLanesModal
        open={openUnmatchedLanesModal}
        noOfLanes={unmatchedLanes?.length}
        handleClose={() => setOpenUnmatchedLanesModal(false)}
        handleSendSubmissions={() => {
          setOpenUnmatchedLanesModal(false);
          setOpenFinalSubmissionModal(true);
        }}
      />
      <FinalSubmissionModal
        open={openFinalSubmissionModal}
        handleClose={() => {
          dispatch(resetBannerActionState());
          setOpenFinalSubmissionModal(false);
        }}
        handleSendSubmissions={handleFinalSubmission}
        submitCapacityMatchIsLoading={submitCapacityMatchIsLoading}
        submitCapacityMatchFailure={submitCapacityMatchFailure}
      />
    </>
  );
}

export default ProcurementViewInProgressState;
